import {useEffect, useState} from 'react'
import {Text as RNText, View} from 'react-native'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {useNavigation} from '@react-navigation/core'

import {type NavigationProp} from '#/lib/routes/types'
import {useKawaiiMode} from '#/state/preferences/kawaii'
import {usePremium} from '#/state/premium'
import {useSession} from '#/state/session'
import {TagList} from '#/view/com/tap/TagList'
import {DesktopFeeds} from '#/view/shell/desktop/Feeds'
import {SidebarInviteCodes} from '#/view/shell/desktop/InviteCodes'
import {DesktopSearch} from '#/view/shell/desktop/Search'
import {SidebarTrendingTopics} from '#/view/shell/desktop/SidebarTrendingTopics'
import {
  atoms as a,
  useGutters,
  useLayoutBreakpoints,
  useTheme,
  web,
} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import {Divider} from '#/components/Divider'
import {VideoCamera_Filled_Corner0_Rounded as VideoCameraIcon} from '#/components/icons/VideoCamera'
import {CENTER_COLUMN_OFFSET} from '#/components/Layout'
import {InlineLinkText, Link} from '#/components/Link'
import {ProgressGuideList} from '#/components/ProgressGuide/List'
import {Text} from '#/components/Typography'

function useWebQueryParams() {
  const navigation = useNavigation()
  const [params, setParams] = useState<Record<string, string>>({})

  useEffect(() => {
    return navigation.addListener('state', e => {
      try {
        const {state} = e.data
        const lastRoute = state.routes[state.routes.length - 1]
        setParams(lastRoute.params)
      } catch (err) {}
    })
  }, [navigation, setParams])

  return params
}

export function DesktopRightNav({routeName}: {routeName: string}) {
  const t = useTheme()
  const {_} = useLingui()
  const navigation = useNavigation<NavigationProp>()
  const {hasSession, currentAccount} = useSession()
  const {isPremium, isLoading: isPremiumLoading} = usePremium()
  const kawaii = useKawaiiMode()
  const gutters = useGutters(['base', 0, 'base', 'wide'])
  const isSearchScreen = routeName === 'Search'
  const webqueryParams = useWebQueryParams()
  const searchQuery = webqueryParams?.q
  const showTrending = !isSearchScreen || (isSearchScreen && !!searchQuery)
  const {rightNavVisible, centerColumnOffset} = useLayoutBreakpoints()
  const isSupportPage = ['Support', 'About', 'Privacy'].includes(routeName)

  const handleJoinPremium = () => {
    navigation.navigate('Premium')
  }

  const [legalOpen, setLegalOpen] = useState(false)

  const handleFeedbackClick = () => {
    if (typeof window !== 'undefined' && (window as any).formbricks) {
      ;(window as any).formbricks.track('feedback')
    }
  }

  if (!rightNavVisible) {
    return null
  }

  const width = centerColumnOffset ? 250 : 300

  return (
    <View
      style={[
        gutters,
        a.gap_lg,
        web({
          position: 'fixed',
          left: '50%',
          transform: [
            {
              translateX: 300 + (centerColumnOffset ? CENTER_COLUMN_OFFSET : 0),
            },
            ...a.scrollbar_offset.transform,
          ],
          width: width + gutters.paddingLeft,
          maxHeight: '100%',
          overflowY: 'auto',
        }),
      ]}>
      {hasSession && currentAccount?.handle && (
        <Link
          to="/1o1"
          label={_(msg`Start 1 on 1 Room`)}
          size="large"
          variant="solid"
          color="primary"
          style={[a.rounded_md, a.justify_center]}>
          <ButtonIcon icon={VideoCameraIcon} position="left" />
          <ButtonText>
            <Trans>Start 1 on 1 Room</Trans>
          </ButtonText>
        </Link>
      )}

      {!isSearchScreen && hasSession && <DesktopSearch />}

      {hasSession && !isPremium && !isPremiumLoading && (
        <Button
          label={_(msg`Join Premium`)}
          onPress={handleJoinPremium}
          size="large"
          variant="solid"
          color="primary"
          style={[a.rounded_md]}>
          <ButtonText>
            <Trans>Join Premium</Trans>
          </ButtonText>
        </Button>
      )}

      {routeName === 'Posts' && (
        <>
          <TagList selectedTag={webqueryParams?.tag} />
          <Divider />
        </>
      )}

      {hasSession && !isSupportPage && (
        <>
          <ProgressGuideList />
          <DesktopFeeds />
          <Divider />
        </>
      )}

      {showTrending && <SidebarTrendingTopics />}

      {hasSession && <SidebarInviteCodes />}

      {hasSession && <Divider />}

      {hasSession && (
        <>
          <Text style={[a.leading_snug, t.atoms.text_contrast_low]}>
            <Text
              onPress={handleFeedbackClick}
              style={[
                {color: '#fd7c21'},
                web({className: 'Feedback', cursor: 'pointer'}),
              ]}
              role="link">
              {_(msg`Feedback`)}
            </Text>
            {' • '}
            <InlineLinkText to="/privacy" label={_(msg`Privacy`)}>
              {_(msg`Privacy`)}
            </InlineLinkText>
            {' • '}
            <InlineLinkText label={_(msg`Help`)} to="/support">
              {_(msg`Help`)}
            </InlineLinkText>
            {' • '}
            <RNText
              onPress={() => setLegalOpen(o => !o)}
              style={[{color: '#fd7c21'}, web({cursor: 'pointer'})]}
              role="button">
              {_(msg`Legal`)}
            </RNText>
            {' • '}
            <InlineLinkText label={_(msg`About`)} to="/support/about">
              {_(msg`About`)}
            </InlineLinkText>
          </Text>
          {legalOpen && (
            <View style={[a.mt_xs, a.gap_xs]}>
              <InlineLinkText
                to="/support/community-guidelines"
                label={_(msg`Community Guidelines`)}>
                {_(msg`Community Guidelines`)}
              </InlineLinkText>
              <InlineLinkText
                to="/support/tos"
                label={_(msg`Terms of Service`)}>
                {_(msg`Terms of Service`)}
              </InlineLinkText>
              <InlineLinkText
                to="/support/privacy-policy"
                label={_(msg`Privacy Policy`)}>
                {_(msg`Privacy Policy`)}
              </InlineLinkText>
              <InlineLinkText
                to="/support/copyright"
                label={_(msg`Copyright Policy`)}>
                {_(msg`Copyright Policy`)}
              </InlineLinkText>
              <InlineLinkText
                to="/support/2257"
                label={_(msg`2257 Disclosure`)}>
                {_(msg`2257 Disclosure`)}
              </InlineLinkText>
            </View>
          )}
        </>
      )}

      {kawaii && (
        <Text style={[t.atoms.text_contrast_medium, {marginTop: 12}]}>
          <Trans>
            Logo by{' '}
            <InlineLinkText
              label={_(msg`Logo by @sawaratsuki.bsky.social`)}
              to="/profile/sawaratsuki.bsky.social">
              @sawaratsuki.bsky.social
            </InlineLinkText>
          </Trans>
        </Text>
      )}
    </View>
  )
}
